const DEBUG_PANEL_SWITCH_KEY = 'h5DebugPanel';
const DEBUG_PANEL_CONFIG_KEY = 'h5DebugPanelConfig';
const DEBUG_PANEL_POSITION_KEY = 'h5DebugPanelPosition';
const DEBUG_PANEL_BUTTON_ID = '__h5DebugPanelButton';
const DEBUG_PANEL_ID = '__h5DebugPanel';

const defaultConfig = {
  pageDebugLogButtonVisible: undefined,
  debugLogVisible: undefined,
  isAdmin: undefined,
  openPreviewUrl: ''
};

/**
 * 获取当前浏览器 window 对象。
 * @returns {Window|null} 当前 window 对象
 */
function getDebugControlWindow() {
  // 非浏览器环境不初始化调试面板。
  if (typeof window === 'undefined') {
    return null;
  }
  return window;
}

/**
 * 判断调试开关值是否表示开启。
 * @param {*} value - 待判断的开关值
 * @returns {boolean} 开关值表示开启时返回 true
 */
function isEnabledValue(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/**
 * 安全读取 localStorage 的值。
 * @param {string} key - localStorage 键名
 * @returns {string|null} 读取到的值
 */
function getLocalStorageItem(key) {
  try {
    const win = getDebugControlWindow();
    // localStorage 不存在时视为未设置。
    if (!win || !win.localStorage) {
      return null;
    }
    return win.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

/**
 * 安全写入 localStorage 的值。
 * @param {string} key - localStorage 键名
 * @param {string} value - 需要写入的值
 * @returns {void}
 */
function setLocalStorageItem(key, value) {
  try {
    const win = getDebugControlWindow();
    // localStorage 不存在时跳过写入。
    if (!win || !win.localStorage) {
      return;
    }
    win.localStorage.setItem(key, value);
  } catch (error) {}
}

/**
 * 安全删除 localStorage 的值。
 * @param {string} key - localStorage 键名
 * @returns {void}
 */
function removeLocalStorageItem(key) {
  try {
    const win = getDebugControlWindow();
    // localStorage 不存在时跳过删除。
    if (!win || !win.localStorage) {
      return;
    }
    win.localStorage.removeItem(key);
  } catch (error) {}
}

/**
 * 判断全局调试面板是否开启。
 * @returns {boolean} 调试面板开启时返回 true
 */
export function isDebugControlPanelEnabled() {
  const win = getDebugControlWindow();
  // 全局变量优先，方便控制台临时打开。
  if (win && isEnabledValue(win.h5DebugPanel)) {
    return true;
  }
  return isEnabledValue(getLocalStorageItem(DEBUG_PANEL_SWITCH_KEY));
}

/**
 * 读取调试面板配置。
 * @returns {Object} 调试面板配置
 */
export function getDebugControlConfig() {
  const savedConfig = getLocalStorageItem(DEBUG_PANEL_CONFIG_KEY);
  // 没有保存过配置时返回默认配置。
  if (!savedConfig) {
    return Object.assign({}, defaultConfig);
  }
  try {
    return Object.assign({}, defaultConfig, JSON.parse(savedConfig));
  } catch (error) {
    return Object.assign({}, defaultConfig);
  }
}

/**
 * 保存调试面板配置。
 * @param {Object} config - 需要保存的调试面板配置
 * @returns {Object} 保存后的完整配置
 */
export function setDebugControlConfig(config) {
  const nextConfig = Object.assign({}, defaultConfig, config || {});
  setLocalStorageItem(DEBUG_PANEL_CONFIG_KEY, JSON.stringify(nextConfig));
  return nextConfig;
}

/**
 * 读取布尔调试开关，未设置时返回传入的默认值。
 * @param {string} key - 配置键名
 * @param {boolean} fallback - 未配置时的默认值
 * @returns {boolean} 解析后的布尔开关值
 */
export function getDebugControlBoolean(key, fallback) {
  const value = getDebugControlConfig()[key];
  // 显式布尔值优先覆盖默认值。
  if (value === true || value === false) {
    return value;
  }
  return fallback;
}

/**
 * 读取样机图调试开关值。
 * @returns {string|null} 返回 '0'、'1' 或 null
 */
export function getDebugPreviewUrlValue() {
  const configValue = getDebugControlConfig().openPreviewUrl;
  // 面板配置有明确值时优先使用。
  if (configValue === '0' || configValue === '1') {
    return configValue;
  }
  const legacyValue = getLocalStorageItem('openPreviewUrl');
  // 兼容历史 localStorage.openPreviewUrl 开关。
  if (legacyValue === '0' || legacyValue === '1') {
    return legacyValue;
  }
  return null;
}

/**
 * 将 CSS 文本设置到元素上。
 * @param {HTMLElement} element - 目标元素
 * @param {string} styleText - CSS 文本
 * @returns {void}
 */
function setStyle(element, styleText) {
  element.style.cssText = styleText;
}

/**
 * 读取调试面板按钮保存位置。
 * @returns {{top:number,right:number}} 按钮位置
 */
function getSavedPanelPosition() {
  const position = {
    top: 72,
    right: 12
  };
  const savedPosition = getLocalStorageItem(DEBUG_PANEL_POSITION_KEY);
  // 没有保存过位置时使用默认位置。
  if (!savedPosition) {
    return position;
  }
  try {
    const parsedPosition = JSON.parse(savedPosition);
    return {
      top: Number(parsedPosition.top) || position.top,
      right:
        parsedPosition.right === 0 || parsedPosition.right
          ? Number(parsedPosition.right)
          : position.right
    };
  } catch (error) {
    return position;
  }
}

/**
 * 将按钮位置限制在当前可视区域内。
 * @param {{top:number,right:number}} position - 原始位置
 * @returns {{top:number,right:number}} 限制后的位置
 */
function clampPanelPosition(position) {
  const win = getDebugControlWindow();
  // 无法读取窗口尺寸时直接返回原位置。
  if (!win) {
    return position;
  }
  const top = Math.min(
    Math.max(0, position.top),
    Math.max(0, win.innerHeight - 34)
  );
  const right = Math.min(
    Math.max(0, position.right),
    Math.max(0, win.innerWidth - 58)
  );
  return {
    top,
    right
  };
}

/**
 * 创建通用按钮元素。
 * @param {string} text - 按钮文案
 * @param {string} background - 背景色
 * @returns {HTMLButtonElement} 按钮元素
 */
function createPanelButton(text, background) {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = text;
  setStyle(
    button,
    `
      border: 0;
      border-radius: 3px;
      padding: 6px 8px;
      background: ${background};
      color: #fff;
      font-size: 12px;
      line-height: 1;
    `
  );
  return button;
}

/**
 * 创建调试面板开关行。
 * @param {string} labelText - 开关文案
 * @param {boolean} checked - 是否选中
 * @param {Function} onChange - 变更回调
 * @returns {HTMLElement} 开关行元素
 */
function createCheckboxRow(labelText, checked, onChange) {
  const label = document.createElement('label');
  setStyle(
    label,
    `
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 38px;
      color: #222;
      font-size: 12px;
    `
  );
  const info = document.createElement('span');
  setStyle(
    info,
    `
      display: flex;
      flex-direction: column;
      gap: 3px;
    `
  );
  const text = document.createElement('span');
  text.textContent = labelText;
  const status = document.createElement('span');
  setStyle(
    status,
    `
      color: #666;
      font-size: 11px;
    `
  );
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.checked = checked;
  setStyle(
    checkbox,
    `
      position: absolute;
      opacity: 0;
      pointer-events: none;
    `
  );
  const switchTrack = document.createElement('span');
  setStyle(
    switchTrack,
    `
      position: relative;
      width: 34px;
      height: 18px;
      border-radius: 18px;
      transition: background 0.2s;
      flex: 0 0 auto;
    `
  );
  const switchThumb = document.createElement('span');
  setStyle(
    switchThumb,
    `
      position: absolute;
      top: 2px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
      transition: left 0.2s;
    `
  );

  /**
   * 根据 checkbox 状态刷新 switch 和状态文案。
   * @returns {void}
   */
  function renderSwitchState() {
    status.textContent = checkbox.checked ? '当前：开启' : '当前：关闭';
    switchTrack.style.background = checkbox.checked ? '#1989fa' : '#c8c9cc';
    switchThumb.style.left = checkbox.checked ? '18px' : '2px';
  }

  checkbox.onchange = function () {
    renderSwitchState();
    onChange(checkbox.checked);
  };
  info.appendChild(text);
  info.appendChild(status);
  switchTrack.appendChild(switchThumb);
  label.appendChild(info);
  label.appendChild(checkbox);
  label.appendChild(switchTrack);
  renderSwitchState();
  return label;
}

/**
 * 创建样机图控制行。
 * @param {string} value - 当前样机图配置
 * @param {Function} onChange - 变更回调
 * @returns {HTMLElement} 控制行元素
 */
function createPreviewSelectRow(value, onChange) {
  const row = document.createElement('label');
  setStyle(
    row,
    `
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 30px;
      color: #222;
      font-size: 12px;
    `
  );
  const info = document.createElement('span');
  setStyle(
    info,
    `
      display: flex;
      flex-direction: column;
      gap: 3px;
    `
  );
  const text = document.createElement('span');
  text.textContent = '样机图';
  const status = document.createElement('span');
  setStyle(
    status,
    `
      color: #666;
      font-size: 11px;
    `
  );
  const select = document.createElement('select');
  setStyle(
    select,
    `
      height: 24px;
      border: 1px solid #d9d9d9;
      border-radius: 3px;
      background: #fff;
      color: #222;
      font-size: 12px;
    `
  );
  [
    { value: '', text: '默认' },
    { value: '1', text: '开启' },
    { value: '0', text: '关闭' }
  ].forEach(function (item) {
    const option = document.createElement('option');
    option.value = item.value;
    option.textContent = item.text;
    select.appendChild(option);
  });
  select.value = value === '0' || value === '1' ? value : '';
  /**
   * 根据 select 状态刷新样机图状态文案。
   * @returns {void}
   */
  function renderPreviewStatus() {
    const statusMap = {
      '': '默认',
      1: '开启',
      0: '关闭'
    };
    status.textContent = '当前：' + statusMap[select.value];
  }
  select.onchange = function () {
    renderPreviewStatus();
    onChange(select.value);
  };
  info.appendChild(text);
  info.appendChild(status);
  row.appendChild(info);
  row.appendChild(select);
  renderPreviewStatus();
  return row;
}

/**
 * 保存兼容历史逻辑的 localStorage 开关。
 * @param {Object} config - 当前调试面板配置
 * @returns {void}
 */
function syncLegacyStorage(config) {
  // 管理员模式开启时同步历史 isAdmin 标记。
  if (config.isAdmin === true) {
    setLocalStorageItem('isAdmin', '1');
  } else if (config.isAdmin === false) {
    removeLocalStorageItem('isAdmin');
  }
  // 样机图有明确覆盖值时同步历史 openPreviewUrl 标记。
  if (config.openPreviewUrl === '0' || config.openPreviewUrl === '1') {
    setLocalStorageItem('openPreviewUrl', config.openPreviewUrl);
  } else {
    removeLocalStorageItem('openPreviewUrl');
  }
}

/**
 * 读取面板展示用配置。
 * @param {Object} options - 初始化选项
 * @returns {Object} 面板展示用配置
 */
function getResolvedPanelConfig(options) {
  const config = getDebugControlConfig();
  return {
    pageDebugLogButtonVisible: getDebugControlBoolean(
      'pageDebugLogButtonVisible',
      !!options.isDev
    ),
    debugLogVisible: getDebugControlBoolean('debugLogVisible', true),
    isAdmin: getDebugControlBoolean(
      'isAdmin',
      getLocalStorageItem('isAdmin') === '1'
    ),
    openPreviewUrl: config.openPreviewUrl || ''
  };
}

/**
 * 创建调试配置面板。
 * @param {Object} options - 初始化选项
 * @param {boolean} options.isDev - 当前是否开发环境
 * @param {Function} options.onPageDebugLogButtonChange - 页面日志按钮变更回调
 * @param {Function} options.onAdminChange - 管理员模式变更回调
 * @returns {HTMLElement} 面板元素
 */
function createDebugControlPanel(options) {
  const panel = document.createElement('div');
  panel.id = DEBUG_PANEL_ID;
  setStyle(
    panel,
    `
      position: fixed;
      top: 112px;
      right: 12px;
      z-index: 2147483647;
      width: 240px;
      padding: 10px;
      background: rgba(255, 255, 255, 0.96);
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 4px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
      display: none;
      font-size: 12px;
    `
  );
  const state = getResolvedPanelConfig(options);

  /**
   * 更新调试配置并同步必要的运行时状态。
   * @param {Object} patch - 配置补丁
   * @returns {void}
   */
  function updateConfig(patch) {
    const nextConfig = setDebugControlConfig(
      Object.assign({}, getDebugControlConfig(), patch)
    );
    syncLegacyStorage(nextConfig);
    // 页面日志按钮需要立刻同步显示状态。
    if (patch.pageDebugLogButtonVisible !== undefined) {
      options.onPageDebugLogButtonChange(!!patch.pageDebugLogButtonVisible);
    }
    // 管理员模式需要同步历史 Store 缓存。
    if (patch.isAdmin !== undefined) {
      options.onAdminChange(!!patch.isAdmin);
    }
  }

  const header = document.createElement('div');
  setStyle(
    header,
    `
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      color: #111;
      font-size: 13px;
      font-weight: bold;
    `
  );
  const title = document.createElement('span');
  title.textContent = '调试面板';
  const closeButton = createPanelButton('关闭', '#666');
  closeButton.onclick = function () {
    panel.style.display = 'none';
  };
  header.appendChild(title);
  header.appendChild(closeButton);

  panel.appendChild(header);
  panel.appendChild(
    createCheckboxRow(
      '页面日志按钮',
      state.pageDebugLogButtonVisible,
      function (checked) {
        updateConfig({
          pageDebugLogButtonVisible: checked
        });
      }
    )
  );
  panel.appendChild(
    createCheckboxRow(
      'debugLog 面板',
      state.debugLogVisible,
      function (checked) {
        updateConfig({
          debugLogVisible: checked
        });
      }
    )
  );
  panel.appendChild(
    createCheckboxRow('管理员模式', state.isAdmin, function (checked) {
      updateConfig({
        isAdmin: checked
      });
    })
  );
  panel.appendChild(
    createPreviewSelectRow(state.openPreviewUrl, function (value) {
      updateConfig({
        openPreviewUrl: value
      });
    })
  );
  return panel;
}

/**
 * 创建调试面板入口按钮。
 * @returns {HTMLElement} 入口按钮元素
 */
function createDebugControlButton() {
  const button = document.createElement('button');
  const position = clampPanelPosition(getSavedPanelPosition());
  button.id = DEBUG_PANEL_BUTTON_ID;
  button.type = 'button';
  button.textContent = '调试';
  setStyle(
    button,
    `
      position: fixed;
      top: ${position.top}px;
      right: ${position.right}px;
      z-index: 2147483647;
      border: 0;
      border-radius: 4px;
      padding: 8px 10px;
      background: #722ed1;
      color: #fff;
      font-size: 12px;
      line-height: 1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      cursor: move;
      user-select: none;
    `
  );
  return button;
}

/**
 * 获取事件坐标。
 * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
 * @returns {{x:number,y:number}} 事件坐标
 */
function getEventPoint(event) {
  // 触摸事件优先读取第一个触点。
  if (event.touches && event.touches.length) {
    return {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY
    };
  }
  return {
    x: event.clientX,
    y: event.clientY
  };
}

/**
 * 设置面板入口按钮位置。
 * @param {HTMLElement} button - 入口按钮元素
 * @param {number} left - 目标 left 坐标
 * @param {number} top - 目标 top 坐标
 * @returns {void}
 */
function setButtonPosition(button, left, top) {
  const rect = button.getBoundingClientRect();
  const maxLeft = Math.max(0, window.innerWidth - rect.width);
  const maxTop = Math.max(0, window.innerHeight - rect.height);
  const nextLeft = Math.min(Math.max(0, left), maxLeft);
  const nextTop = Math.min(Math.max(0, top), maxTop);
  button.style.left = '';
  button.style.right = window.innerWidth - nextLeft - rect.width + 'px';
  button.style.top = nextTop + 'px';
}

/**
 * 保存入口按钮当前位置。
 * @param {HTMLElement} button - 入口按钮元素
 * @returns {void}
 */
function saveButtonPosition(button) {
  const style = window.getComputedStyle(button);
  setLocalStorageItem(
    DEBUG_PANEL_POSITION_KEY,
    JSON.stringify({
      top: parseFloat(style.top) || 0,
      right: parseFloat(style.right) || 0
    })
  );
}

/**
 * 同步配置面板位置到入口按钮附近。
 * @param {HTMLElement} button - 入口按钮元素
 * @param {HTMLElement} panel - 配置面板元素
 * @returns {void}
 */
function syncPanelPosition(button, panel) {
  const rect = button.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const top = Math.min(
    rect.bottom + 6,
    Math.max(0, window.innerHeight - panelRect.height)
  );
  const right = Math.min(
    Math.max(0, window.innerWidth - rect.right),
    Math.max(0, window.innerWidth - panelRect.width)
  );
  panel.style.top = top + 'px';
  panel.style.right = right + 'px';
}

/**
 * 绑定入口按钮拖动和点击事件。
 * @param {HTMLElement} button - 入口按钮元素
 * @param {HTMLElement} panel - 配置面板元素
 * @returns {void}
 */
function bindPanelButtonDrag(button, panel) {
  let dragging = false;
  let moved = false;
  let startPoint = null;
  let startRect = null;

  /**
   * 开始拖动入口按钮。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleStart(event) {
    dragging = true;
    moved = false;
    startPoint = getEventPoint(event);
    startRect = button.getBoundingClientRect();
    event.preventDefault();
  }

  /**
   * 移动入口按钮。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleMove(event) {
    // 未进入拖动状态时不处理移动事件。
    if (!dragging || !startPoint || !startRect) {
      return;
    }
    const point = getEventPoint(event);
    const diffX = point.x - startPoint.x;
    const diffY = point.y - startPoint.y;
    // 超过阈值后才视为拖动，避免点击抖动。
    if (Math.sqrt(diffX * diffX + diffY * diffY) > 4) {
      moved = true;
    }
    setButtonPosition(button, startRect.left + diffX, startRect.top + diffY);
    // 面板打开时跟随入口按钮移动。
    if (panel.style.display !== 'none') {
      syncPanelPosition(button, panel);
    }
    event.preventDefault();
  }

  /**
   * 结束拖动或触发点击。
   * @param {MouseEvent|TouchEvent} event - 鼠标或触摸事件
   * @returns {void}
   */
  function handleEnd(event) {
    // 未进入拖动状态时不处理结束事件。
    if (!dragging) {
      return;
    }
    dragging = false;
    // 拖动时保存位置，未拖动时切换面板。
    if (moved) {
      saveButtonPosition(button);
    } else {
      panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
      syncPanelPosition(button, panel);
    }
    event.preventDefault();
  }

  button.addEventListener('mousedown', handleStart);
  button.addEventListener('touchstart', handleStart, { passive: false });
  document.addEventListener('mousemove', handleMove);
  document.addEventListener('touchmove', handleMove, { passive: false });
  document.addEventListener('mouseup', handleEnd);
  document.addEventListener('touchend', handleEnd);
}

/**
 * 移除已有调试面板。
 * @param {Document} doc - 当前文档对象
 * @returns {void}
 */
function removeExistingPanel(doc) {
  const oldButton = doc.getElementById(DEBUG_PANEL_BUTTON_ID);
  const oldPanel = doc.getElementById(DEBUG_PANEL_ID);
  // 旧入口按钮存在时先移除，避免热更新后重复生成。
  if (oldButton && oldButton.parentNode) {
    oldButton.parentNode.removeChild(oldButton);
  }
  // 旧面板存在时先移除，避免热更新后事件和状态残留。
  if (oldPanel && oldPanel.parentNode) {
    oldPanel.parentNode.removeChild(oldPanel);
  }
}

/**
 * 开启调试面板并刷新当前页面上的面板入口。
 * @param {Object} options - 初始化选项
 * @returns {Object} 当前调试面板配置
 */
function enableDebugControlPanel(options) {
  const win = getDebugControlWindow();
  setLocalStorageItem(DEBUG_PANEL_SWITCH_KEY, '1');
  // 当前页面立即标记开启，避免只写 localStorage 后需要刷新页面。
  if (win) {
    win.h5DebugPanel = 1;
  }
  initDebugControlPanel(options);
  return getDebugControlConfig();
}

/**
 * 初始化全局调试控制面板。
 * @param {Object} options - 初始化选项
 * @param {boolean} options.isDev - 当前是否开发环境
 * @param {Function} options.onPageDebugLogButtonChange - 页面日志按钮变更回调
 * @returns {void}
 */
export function initDebugControlPanel(options = {}) {
  const win = getDebugControlWindow();
  // 非浏览器环境无需初始化。
  if (!win || !win.document) {
    return;
  }
  win.__refreshH5DebugPanel = function () {
    initDebugControlPanel(options);
  };
  win._debug = function () {
    return enableDebugControlPanel(options);
  };
  // body 尚未创建时延后初始化。
  if (!win.document.body) {
    win.document.addEventListener(
      'DOMContentLoaded',
      function () {
        initDebugControlPanel(options);
      },
      { once: true }
    );
    return;
  }
  removeExistingPanel(win.document);
  // 调试总开关未开启时只保留刷新入口，不创建可见面板。
  if (!isDebugControlPanelEnabled()) {
    return;
  }
  syncLegacyStorage(getDebugControlConfig());
  const button = createDebugControlButton();
  const panel = createDebugControlPanel(
    Object.assign(
      {
        isDev: false,
        onAdminChange: function () {},
        onPageDebugLogButtonChange: function () {}
      },
      options
    )
  );
  bindPanelButtonDrag(button, panel);
  win.document.body.appendChild(button);
  win.document.body.appendChild(panel);
}

export default {
  getDebugControlBoolean,
  getDebugControlConfig,
  getDebugPreviewUrlValue,
  initDebugControlPanel,
  isDebugControlPanelEnabled,
  setDebugControlConfig
};
